import { Spin } from 'antd';
import classNames from 'classnames';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import { SELECTION_CIRCLES, UNIT_MEASURE } from '@common/constants';
import formatUOM from '@common/helpers/formatUOM';
import { CornerRadiusModel } from '@common/models';
import { getProductDescription } from '@common/utils';
import { formatPriceUnitOrFree } from '@common/utils/formatPriceUnitOrFree';
import { SectionSubtitle } from '@core/components';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import ImageUtils from '@core/utils/ImageUtils';
import { useCornerRadii } from '@ee-api/corner-radii-api/hooks/useCornerRadii';
import { useSelectedCornerRadius } from '@ee-api/corner-radii-api/hooks/useSelectedCornerRadius';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { DrawingShapeContext } from '@ee-context';
import { updateSelectedCornerRadius } from '@redux/slices/cornerRadiiSlice';
import { resetSelectedCustomSide } from '@redux/slices/customSideSlice';
import { resetSelectedDiagonalCorner } from '@redux/slices/diagonalCornerSlice';
import { updateSelectionCircles } from '@redux/slices/fabricSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

interface CornerRadiusItemProps {
    cornerRadius: CornerRadiusModel;
}

export function CornerRadius(): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const zipcode = useAppSelector(selectZipcode);
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { drawingShapes } = useContext(DrawingShapeContext);

    const { cornerRadii, isFetchingCornerRadii } = useCornerRadii();

    const { selectedValue: selectedCornerRadius } = useSelectedCornerRadius();
    const { trackSelectRadius } = useAnalyticsEvents();

    const CornerRadiusItem = ({ cornerRadius }: CornerRadiusItemProps) => {
        const description = getProductDescription(cornerRadius);
        const unitOfMeasure = formatUOM(cornerRadius.unitOfMeasure || UNIT_MEASURE.CORNER_RADIUS);

        const isSelected = drawingShapes.length > 1 && selectedCornerRadius?.productId === cornerRadius.productId;

        const selectManualCornerRadius = (corner: CornerRadiusModel) => {
            if (isSelected) {
                return;
            }
            trackSelectRadius({ radius: corner });
            dispatch(resetSelectedCustomSide());
            dispatch(resetSelectedDiagonalCorner());
            dispatch(updateSelectedCornerRadius(corner));
            dispatch(updateSelectionCircles(SELECTION_CIRCLES.FOR_ROUNDED_RADII));
        };

        return (
            <div
                className={classNames('card-option single-action-card', {
                    selected: isSelected,
                })}
                onClick={() => selectManualCornerRadius(cornerRadius)}
            >
                <img
                    className="card-option__image"
                    alt="corner-radius"
                    src={ImageUtils.imgUrl(cornerRadius.imageUrl, 300)}
                />
                <div className="name">
                    {/* TODO the name value is being pre-formatted for these cards, but the consumerDesc does not match
                        to be consistent we should use consumerDesc and have the SMEs fix the data
                        leaving the name for now to allow us to review with the SMEs first
                     */}
                    {description}
                </div>

                <div className="price">{formatPriceUnitOrFree(cornerRadius.price, unitOfMeasure)}</div>
            </div>
        );
    };

    if (cornerRadii.length && drawingShapes.length > 1 && selectedMaterial && zipcode) {
        return (
            <>
                <SectionSubtitle
                    name={t('SCROLLABLE_AREA.CORNER_RADIUS.TITLE')}
                    description={t('SCROLLABLE_AREA.CORNER_RADIUS.DESCRIPTION')}
                    showLine
                />
                {isFetchingCornerRadii ? (
                    <Spin style={{ fontSize: 24 }} />
                ) : (
                    <div className="card-single-select">
                        {cornerRadii.map((cornerRadius, id) => (
                            <CornerRadiusItem key={id} cornerRadius={cornerRadius} />
                        ))}
                    </div>
                )}
            </>
        );
    } else {
        return <></>;
    }
}
